/**
 * Ce qu'il manque à une fiche, dit en haut, puis section par section, puis en
 * bas quand il ne manque plus rien.
 *
 * ## Pourquoi trois endroits et pas un
 *
 * Le bandeau annonce le travail : combien, et où. Sans lui, un parent ouvre
 * sept sections repliées pour découvrir laquelle réclame quelque chose.
 *
 * Le compteur par partie dit où aller sans avoir à tout relire.
 *
 * La barre du bas mène au parcours de vérification. Elle reste visible pendant
 * qu'on descend, parce que sur un téléphone la fin de la page est à sept écrans
 * du début : un bouton posé là-bas ne se trouve qu'en cherchant.
 *
 * ## Cette page ne valide plus rien elle-même
 *
 * Elle l'a fait un temps : un bouton « Tout est exact » en bas, après sept
 * sections repliées. Le parent voyait dans quelle section il manquait quelque
 * chose, jamais dans quel champ, et devait rouvrir, modifier, enregistrer,
 * refermer, section par section, avant de redescendre valider.
 *
 * La vérification est donc partie ailleurs — `/kids/:id/verifier`, le même
 * parcours en cinq étapes que la création, où ce qui manque est en orange dans
 * le champ même. Ici on consulte, et on corrige une chose au passage.
 *
 * ## Le numéro de registre national ne bloque pas
 *
 * Il apparaît dans le bandeau, en gris, sous les autres. 876 enfants n'en ont
 * pas : en faire un blocage fermerait la porte à une famille sur trois pour
 * une information qui ne décide que de l'attestation fiscale.
 */

import { Link } from 'react-router-dom';
import { AlertCircle, ArrowRight, Info } from 'lucide-react';
import { type CompletudeEnfant } from '../../../lib/completude';
import { ORDRE_ETAPES, TITRE_ETAPE, etapeDuManque, manquesParEtape, type EtapeFiche } from './sections';

/** La pastille posée sur l'en-tête d'une section repliée. */
export const BadgeSection = ({ manquants }: { manquants: number }) => {
  if (manquants === 0) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-900 ring-1 ring-inset ring-amber-200">
      <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
      {manquants === 1 ? '1 à compléter' : `${manquants} à compléter`}
    </span>
  );
};

/**
 * Le bandeau de tête : ce qui manque, groupé par section.
 *
 * Il n'affiche pas les messages un par un quand il y en a beaucoup — une fiche
 * jamais remplie en compte dix, et dix phrases rouges à l'ouverture d'une page
 * font refermer l'onglet. Au-delà de quatre, il compte par section.
 */
export const BandeauManques = ({
  completude,
  prenom,
  kidId,
  onAller,
}: {
  completude: CompletudeEnfant;
  prenom: string;
  kidId: string;
  /** Ouvre l'étape où le manque se corrige. */
  onAller: (etape: EtapeFiche) => void;
}) => {
  const { bloquants, signales, perimee, complet } = completude;
  const enfant = prenom.trim() || 'votre enfant';

  if (complet && !perimee && signales.length === 0) return null;

  const parEtape = manquesParEtape(completude);
  const etapes = ORDRE_ETAPES.filter((e) => parEtape[e]);
  const detaille = bloquants.length > 0 && bloquants.length <= 4;

  return (
    <div className="mb-6 space-y-3">
      {bloquants.length > 0 && (
        <section
          aria-labelledby="manques"
          className="rounded-xl bg-amber-50 p-4 ring-1 ring-amber-200"
        >
          <h2
            id="manques"
            className="flex items-center gap-2 text-base font-semibold text-amber-900"
          >
            <AlertCircle className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
            {bloquants.length === 1
              ? 'Une information manque'
              : `${bloquants.length} informations manquent`}
          </h2>
          <p className="mt-1 text-sm text-amber-900/80">
            Nous en avons besoin pour accueillir {enfant}. Tant qu’elles manquent, vous ne pouvez
            pas l’inscrire à un stage.
          </p>

          {/*
            Le chemin principal : reparcourir la fiche en cinq etapes, ou les
            manques sont signales dans le champ meme. La liste qui suit reste
            pour qui vient corriger UNE chose et repartir.
          */}
          <Link
            to={`/kids/${kidId}/verifier`}
            className="btn-primary mt-4 flex min-h-[48px] w-full items-center justify-center"
          >
            Vérifier la fiche · environ 1 minute
            <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
          </Link>

          <p className="mt-3 text-xs font-medium uppercase tracking-wide text-amber-900/70">
            Ou corriger directement&nbsp;:
          </p>

          <ul className="mt-2 space-y-1.5">
            {detaille
              ? bloquants.map((m) => (
                  <li key={m.cle}>
                    <button
                      type="button"
                      onClick={() => onAller(etapeDuManque(m))}
                      className="flex min-h-[44px] w-full items-center justify-between gap-3 rounded-lg bg-white px-3 py-2 text-left text-sm ring-1 ring-amber-200 hover:bg-amber-100/50"
                    >
                      <span className="text-gray-900">{m.libelle}</span>
                      <span className="flex-shrink-0 text-xs font-medium text-amber-700">
                        {TITRE_ETAPE[etapeDuManque(m)]}
                      </span>
                    </button>
                  </li>
                ))
              : etapes.map((e) => (
                  <li key={e}>
                    <button
                      type="button"
                      onClick={() => onAller(e)}
                      className="flex min-h-[44px] w-full items-center justify-between gap-3 rounded-lg bg-white px-3 py-2 text-left text-sm ring-1 ring-amber-200 hover:bg-amber-100/50"
                    >
                      <span className="font-medium text-gray-900">{TITRE_ETAPE[e]}</span>
                      <span className="flex-shrink-0 text-xs font-medium text-amber-700">
                        {parEtape[e]} à compléter
                      </span>
                    </button>
                  </li>
                ))}
          </ul>
        </section>
      )}

      {bloquants.length === 0 && perimee && (
        <section className="rounded-xl bg-amber-50 p-4 ring-1 ring-amber-200">
          <h2 className="flex items-center gap-2 text-base font-semibold text-amber-900">
            <AlertCircle className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
            À vérifier
          </h2>
          <p className="mt-1 text-sm text-amber-900/80">
            Relisez les informations de {enfant} — allergies, médecin, personnes autorisées à
            venir le ou la chercher — et confirmez qu’elles sont exactes.
          </p>
          <Link
            to={`/kids/${kidId}/verifier`}
            className="btn-primary mt-4 flex min-h-[48px] w-full items-center justify-center"
          >
            Vérifier la fiche · environ 1 minute
            <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
          </Link>
        </section>
      )}

      {signales.length > 0 && (
        <section className="rounded-xl bg-gray-50 p-4 ring-1 ring-gray-200">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <Info className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            Facultatif
          </h2>
          <ul className="mt-1.5 space-y-1">
            {signales.map((m) => (
              <li key={m.cle} className="text-sm text-gray-600">
                <button
                  type="button"
                  onClick={() => onAller(etapeDuManque(m))}
                  className="text-left underline decoration-gray-300 underline-offset-2 hover:text-gray-900"
                >
                  {m.libelle}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

/**
 * La barre du bas : rejoindre le parcours de vérification.
 *
 * Collée au bas de l'écran (`sticky`) et non à la fin du document : la fiche
 * fait sept écrans sur un téléphone, et le parent doit pouvoir partir vérifier
 * dès qu'il l'a décidé, sans redescendre chercher le bouton.
 */
export const BarreVerifier = ({
  completude,
  kidId,
}: {
  completude: CompletudeEnfant;
  kidId: string;
}) => {
  const { bloquants, perimee, complet } = completude;

  /* Rien à faire : la barre ne s'affiche pas, elle n'aurait rien à proposer. */
  if (complet && !perimee) return null;

  return (
    /*
      `-mx-4 px-4` compense exactement le `px-4` de la page : la barre va d'un
      bord a l'autre, et son contenu reste aligne sur les sections.
    */
    <div className="sticky bottom-0 z-10 -mx-4 mt-8 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
        <p className="text-sm text-gray-600 sm:mr-auto">
          {complet
            ? 'Ces informations datent de plus de six semaines.'
            : bloquants.length === 1
              ? 'Il reste une information à compléter.'
              : `Il reste ${bloquants.length} informations à compléter.`}
        </p>
        <Link
          to={`/kids/${kidId}/verifier`}
          className="btn-primary inline-flex min-h-[48px] items-center justify-center"
        >
          Vérifier la fiche
          <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
};
